// A bundle groups open stops at the same point on the map that are handled at once. Bundles are not stored, in the database
// these remain separate stops. Shared by the messenger app and the main application.

interface Stop {
    id: string
    orderId: string
    index: number
    type: 'pickup' | 'delivery'
    postalCode?: string
    nr?: string | number
    addition?: string
    position?: {lat?: number | string, lng?: number | string}
    isReady?: boolean
    isNextStop?: boolean
    skipsFixedZone?: boolean
    locked?: boolean
    anchor?: unknown
    colli?: unknown[]
    bundleId?: string
    stopNumber?: string | number
    minimumAge?: number | string | null
    mailboxDelivery?: boolean
    customOptions?: string[]
    [key: string]: unknown
}

export interface StopBundle {
    id: string
    isBundle: true
    type: 'pickup' | 'delivery'
    addresses: Stop[]
    readyAddresses: Stop[]
    isReady: boolean
    isNextStop: boolean
    skipsFixedZone: boolean
    locked: boolean
    anchor: boolean
    colliCount: number
}

type BundleKeyStop = Partial<Pick<Stop, 'type' | 'stopNumber' | 'position' | 'postalCode' | 'nr' | 'addition'>>

type AddressKeyStop = {postalCode?: string | null, nr?: string | number | null, addition?: string | null}

interface NotPossibleReasons {
    reasons: string[]
    specifications: {reason: string, specification: string}[]
}

// Stops at different addresses are never one stop, even at the same coordinates. The address is the postal code and
// the house number with addition, without spaces and case insensitive ("50A" equals "50" with addition " a"). Street
// and city are left out, these are often entered differently for the same address. Without postal code or house
// number the key is empty, such a stop is never combined with another stop. The route optimization
// (VRP_create_npData.py) uses the same key.
function addressKey(address: AddressKeyStop): string {
    const postalCode = `${address.postalCode ?? ''}`.replace(/\s/g, '').toUpperCase()
    const nr = `${address.nr ?? ''}${address.addition ?? ''}`.replace(/\s/g, '').toLowerCase()

    if (!postalCode || !`${address.nr ?? ''}`.trim()) {
        return ''
    }

    return `${postalCode}|${nr}`
}

// Consecutive stops at exactly the same coordinates and the same address (addressKey) get the same stop number from
// the route planning (compareAddresses in server/functions/route/route.ts). A stop number can also come from the API,
// and existing routes can have a shared stop number from before the address was compared, so the coordinates and the
// address are checked as well: different points or addresses that happen to share a stop number are no bundle. The
// coordinates are compared as text, the same way for every source. Stops without coordinates, without address key or
// without stop number (route not planned yet) are never bundled.
function bundleKey(address: BundleKeyStop): string {
    const stopNumber = `${address.stopNumber ?? ''}`.trim()
    const lat = `${address.position?.lat ?? ''}`.trim()
    const lng = `${address.position?.lng ?? ''}`.trim()
    const key = addressKey(address)

    if (!stopNumber || !lat || !lng || !key) {
        return ''
    }

    return `${address.type} ${stopNumber} ${lat},${lng} ${key}`
}

// Groups consecutive stops of the same type with the same stop number at the same coordinates and address. Every group of two or
// more stops is a bundle. The stops are not changed.
function groupStops<T extends BundleKeyStop>(addresses: T[]): T[][] {
    const groups: T[][] = []

    addresses.forEach((address) => {
        const lastGroup = groups[groups.length - 1]
        const key = bundleKey(address)

        if (lastGroup && key && bundleKey(lastGroup[0]) === key) {
            lastGroup.push(address)
        } else {
            groups.push([address])
        }
    })

    return groups
}

// The number of stops in a route, where a bundle counts as one stop
function countStops(addresses: BundleKeyStop[]): number {
    return groupStops(addresses).length
}

// Groups consecutive open stops into bundles (see groupStops). Returns the stops in the same order, with every group
// of two or more stops replaced by one bundle. All stops of a bundle count as next stop when one of them is the next
// stop of the route.
function getStopBundles(addresses: Stop[], nextStopAddress?: Stop): (Stop | StopBundle)[] {
    addresses.forEach((address) => {
        delete address.bundleId
    })

    return groupStops(addresses).map((group) => {
        if (group.length === 1) {
            return group[0]
        }

        const id = `bundle ${group[0].id}`
        const isNextStop = !!nextStopAddress && group.includes(nextStopAddress)

        group.forEach((address) => {
            address.bundleId = id

            if (isNextStop) {
                address.isNextStop = true
                address.skipsFixedZone = false
            }
        })

        const readyAddresses = group.filter((address) => address.isReady)

        return {
            id,
            isBundle: true,
            type: group[0].type,
            addresses: group,
            readyAddresses,
            isReady: readyAddresses.length > 0,
            isNextStop: group.every((address) => address.isNextStop),
            skipsFixedZone: group.some((address) => address.skipsFixedZone),
            locked: group.every((address) => address.locked),
            anchor: group.some((address) => !!address.anchor),
            colliCount: group.reduce((count, address) => count + (address.colli?.length || 0), 0)
        }
    })
}

function isBundle(item: Stop | StopBundle): item is StopBundle {
    return item.isBundle === true
}

function findBundle(items: (Stop | StopBundle)[] | undefined, orderId: string, addressIndex: number): StopBundle | undefined {
    return items?.filter(isBundle).find((bundle) => bundle.addresses.some((address) => address.orderId === orderId && address.index === addressIndex))
}

// Options of a bundle are the sum of the options of its stops, except mailbox delivery which every stop must have
function combineOptions(addresses: Stop[]) {
    const some = (key: string) => addresses.some((address) => !!address[key])
    const mailboxStops = addresses.filter((address) => address.mailboxDelivery)

    return {
        gpsRequired: some('gpsRequired'),
        signatureRequired: some('signatureRequired'),
        nameRequired: some('nameRequired'),
        signatureNeighbors: some('signatureNeighbors'),
        statedAddressOnly: some('statedAddressOnly'),
        mailboxDelivery: addresses.length > 0 && mailboxStops.length === addresses.length,
        mixedMailboxDelivery: mailboxStops.length > 0 && mailboxStops.length < addresses.length,
        refrigerated: some('refrigerated'),
        scanRequired: some('scanRequired'),
        imageRequired: some('imageRequired'),
        minimumAge: Math.max(0, ...addresses.map((address) => parseInt(`${address.minimumAge ?? ''}`) || 0)) || null,
        idCheck: some('idCheck'),
        verificationCode: some('verificationCode'),
        customOptions: [...new Set(addresses.flatMap((address) => address.customOptions || []))]
    }
}

// Only reasons (and specifications) that every customer in the bundle allows can be chosen for the whole bundle
function intersectNotPossibleReasons(reasonsList: NotPossibleReasons[]): NotPossibleReasons {
    if (!reasonsList.length) {
        return {reasons: [], specifications: []}
    }

    const [first, ...others] = reasonsList

    return {
        reasons: first.reasons.filter((reason) => others.every((other) => other.reasons.includes(reason))),
        specifications: first.specifications.filter((specification) => others.every((other) => {
            return other.specifications.some((otherSpecification) => otherSpecification.reason === specification.reason && otherSpecification.specification === specification.specification)
        }))
    }
}

function stopPath(target: Stop | StopBundle): string {
    if (isBundle(target)) {
        return `/koerier/bundel/${target.addresses[0].orderId}/${target.addresses[0].index}`
    }

    return `/koerier/${target.type === 'pickup' ? 'ophalen' : 'bezorgen'}/${target.orderId}/${target.index}`
}

export default {
    addressKey,
    bundleKey,
    groupStops,
    countStops,
    getStopBundles,
    findBundle,
    combineOptions,
    intersectNotPossibleReasons,
    stopPath
}
